/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import * as React from 'react';
import styled from 'styled-components';
import { memo } from '@web-stories-wp/react';

/**
 * Internal dependencies
 */
import { ReorderableSeparator, ReorderableItem } from '../../../reorderable';
import { useStory } from '../../../../app';
import { LAYER_HEIGHT } from './constants';
import GroupLayer from './groupLayer';

const LayerSeparator = styled(ReorderableSeparator)`
  height: ${LAYER_HEIGHT}px;
  margin-block: -${LAYER_HEIGHT / 2}px;
  padding-block: ${LAYER_HEIGHT / 2}px;
`;

export const ReorderableGroupLayer = memo(function ReorderableGroup({
  id,
  reorderingGroupId,
  nestedOffsetCalcFunc,
  position,
  handleStartReordering,
  previousElementGroupId,
}: {
  id: string;
  reorderingGroupId: string;
  nestedOffsetCalcFunc: (evt) => number;
  position: number;
  handleStartReordering: ({ id }: { id: string }) => void;
  previousElementGroupId?: string;
}) {
  const { groups } = useStory(({ state }) => ({
    groups: state.currentPage?.groups || {},
  }));

  const previousGroup = groups[previousElementGroupId];

  return (
    <>
      {/* This separator is used to move an element between the previous element/group and the current group */}
      <LayerSeparator
        groupId={reorderingGroupId && reorderingGroupId === id ? id : null}
        position={position + 1}
      />

      {/* This one is used to move an element inside the previous group (at the last position) */}
      {!previousGroup ||
      previousGroup.isCollapsed ||
      reorderingGroupId ? null : (
        <LayerSeparator
          groupId={previousElementGroupId}
          nestedOffset
          nestedOffsetCalcFunc={nestedOffsetCalcFunc}
          position={position + 1}
          isNested={previousElementGroupId}
        />
      )}
      <ReorderableItem
        position={position}
        data={{ group: id }}
        onStartReordering={handleStartReordering({ id })}
      >
        <GroupLayer groupId={id} />
      </ReorderableItem>
    </>
  );
});
